/**
 * GDPR script-gating + Consent Mode engine.
 *
 * Opt-in script gating (theme/merchant uses this):
 *   <script type="text/plain" data-acb-category="statistics"
 *           data-acb-src="https://www.googletagmanager.com/gtag/js?id=G-XXX"></script>
 *   <script type="text/plain" data-acb-category="marketing">
 *     fbq('init','PIXEL_ID'); fbq('track','PageView');
 *   </script>
 * Browsers ignore type="text/plain", so the script never executes until this
 * engine activates it after consent for the matching category.
 *
 * Consent Mode (Google / Microsoft) syncs granted/denied storage signals with
 * advertising and analytics tags. Cookie cleanup removes denied-category
 * cookies after reject / withdraw. There is no preemptive network auto-blocker.
 *
 * Categories: necessary | statistics | marketing | preferences
 */

import { STORAGE_KEY } from './constants';
import { getRegionStateSync, logVisitorRegion } from './region';

// Best-effort cookie cleanup list per category.
// Names are matched against document.cookie names. Cookies set as HttpOnly or
// on a different domain than the storefront cannot be removed from JS.
const COOKIE_PATTERNS = {
  statistics: [
    // Google Analytics / GTM
    /^_ga(_|$)/,
    /^_gid$/,
    /^_gat(_|$)/,
    /^_dc_gtm_/,
    /^AMP_TOKEN$/,
    /^__utm[abctvz]$/,
    // Hotjar
    /^_hjSession/i,
    /^_hjid$/,
    /^_hjAbsoluteSessionInProgress$/,
    /^_hjFirstSeen$/,
    /^_hjIncludedInPageviewSample$/,
    /^_hjIncludedInSessionSample$/,
    /^_hjTLDTest$/,
    // Microsoft Clarity
    /^_clck$/,
    /^_clsk$/,
    /^CLID$/,
    // Matomo / Piwik
    /^_pk_/,
    // Mixpanel
    /^mp_/,
    // Amplitude
    /^amplitude_/i,
    // Segment
    /^ajs_anonymous_id$/,
    /^ajs_user_id$/,
    /^ajs_group_id$/,
    // Plausible / Fathom (cookieless usually, but defensive)
    /^plausible_ignore$/,
    // PostHog
    /^ph_/,
    // Snowplow
    /^_sp_/,
    /^sp_/,
    // RudderStack
    /^rl_/,
    // Kissmetrics
    /^km_/,
    // LogRocket
    /^lr_/,
    // Shopify first-party analytics (NOT necessary)
    /^_shopify_y$/,
    /^_shopify_s$/,
    /^_shopify_d$/,
    /^_shopify_ga$/,
    /^_shopify_evids$/,
    /^_y$/,
    /^_s$/,
    /^_landing_page$/,
    /^_orig_referrer$/,
    // Adobe Analytics / Experience Cloud
    /^s_cc$/,
    /^s_sq$/,
    /^s_vi$/,
    /^s_fid$/,
    /^s_ppv$/,
    /^s_ppvl$/,
    /^AMCV_/,
    /^AMCVS_/,
    // Yandex Metrica
    /^_ym_/,
    /^yabs-sid$/,
    // Heap
    /^_hp2_/,
    // Crazy Egg
    /^_ce\./,
    /^_CEFT$/,
    /^_ceir$/,
    /^cer\.(s|v)$/,
    // FullStory
    /^fs_uid$/,
    /^_fs_uid$/,
    // Mouseflow
    /^mf_user$/,
    /^mf_session$/,
    // Lucky Orange
    /^__lo_cid_/,
    /^__lotr/,
    // Optimizely
    /^optimizelyEndUserId$/,
    /^optimizely/,
    // VWO
    /^_vwo_/,
    /^_vis_opt_/,
    // Quantcast
    /^_qca$/,
    // HubSpot tracking
    /^__hstc$/,
    /^__hssc$/,
    /^__hssrc$/,
    /^hubspotutk$/,
    // Statcounter
    /^is_visitor_unique$/,
    // Chartbeat
    /^_chartbeat/,
    // Comscore
    /^UIDR$/,
    // Generic test cookie names
    /^cookietest$/i,
  ],
  marketing: [
    // Meta / Facebook
    /^_fbp$/,
    /^_fbc$/,
    /^datr$/,
    // Google Ads / DoubleClick
    /^_gcl_/,
    /^IDE$/,
    /^test_cookie$/,
    /^DSID$/,
    /^FLC$/,
    /^AID$/,
    /^TAID$/,
    /^exchange_uid$/,
    /^__gads$/,
    /^__gpi$/,
    /^__Secure-3PAPISID$/,
    /^__Secure-3PSID$/,
    /^__Secure-3PSIDCC$/,
    // Microsoft / Bing Ads
    /^MUID$/,
    /^MUIDB$/,
    /^_uetsid$/,
    /^_uetvid$/,
    /^ANONCHK$/,
    /^SRM_B$/,
    // Twitter / X
    /^personalization_id$/,
    /^guest_id$/,
    /^muc_ads$/,
    /^_twitter_sess$/,
    // LinkedIn
    /^li_(at|gc|mc|sugr|fat_id|rm|oat)/,
    /^lidc$/,
    /^bcookie$/,
    /^bscookie$/,
    /^UserMatchHistory$/,
    /^AnalyticsSyncHistory$/,
    // TikTok
    /^_ttp$/,
    /^tt_appInfo$/,
    /^tt_webid/,
    /^tt_csrf_token$/,
    // Snapchat
    /^_scid$/,
    /^sc_at$/,
    // Pinterest
    /^_pin_unauth$/,
    /^_pinterest_/,
    /^_epik$/,
    // Reddit Ads
    /^_rdt_uuid$/,
    // Quora
    /^_q_d$/,
    /^m-uid$/,
    // Outbrain
    /^outbrain_cid_fetch$/,
    /^obuid$/,
    // Taboola
    /^t_gid$/,
    /^taboola_/,
    // Criteo
    /^cto_/,
    /^criteo_/,
    // AddThis / ShareThis / Oracle
    /^__atuvc$/,
    /^__atuvs$/,
    /^__stid$/,
    /^__unam$/,
    // Klaviyo
    /^__kla_id$/,
    // Drift
    /^drift_/,
    /^driftt_/,
    // Mailchimp / Mandrill
    /^_mcid$/,
    /^mc_cid$/,
    /^mc_eid$/,
    /^mc_landing$/,
    // Attentive / Omnisend / Braze
    /^__attentive/,
    /^omnisend/,
    /^braze/,
    // Intercom / Gorgias
    /^intercom-/i,
    /^gorgias/,
    // Yotpo / Smile loyalty
    /^yotpo/i,
    /^smile_/,
    // Yahoo / Verizon Media
    /^A1S$/,
    // Adroll
    /^__adroll/,
    /^__ar_v4$/,
    // Bidswitch / OpenX / AppNexus / Rubicon
    /^bsw_/,
    /^OAID$/,
    /^uuid2$/,
    /^anj$/,
    /^icu$/,
    // Amazon Ads
    /^ad-id$/,
    /^ad-privacy$/,
    // Shopify marketing/attribution (NOT necessary)
    /^_shopify_sa_p$/,
    /^_shopify_sa_t$/,
    // Generic ad tags
    /^ads\//,
  ],
  preferences: [
    // Add merchant-specific preference cookies here as needed.
  ],
};

// ---------- merchant-supplied extensions ----------
//
// Merchants can pass extra cookie patterns via the
// `accessProCookieBannerSettings` global, e.g.:
//   window.accessProCookieBannerSettings = {
//     ...,
//     cookie_patterns_extra:  { statistics: ['^myCookie$'], marketing: [...] },
//   };
// cookie_patterns_extra entries are compiled to RegExp.

function mergeArrayMap(base, extra, mapValue) {
  if (!extra || typeof extra !== 'object') return;
  for (const cat of Object.keys(extra)) {
    if (!Array.isArray(base[cat])) continue;
    const list = extra[cat];
    if (!Array.isArray(list)) continue;
    for (const v of list) {
      const mapped = mapValue ? mapValue(v) : v;
      if (mapped != null) base[cat].push(mapped);
    }
  }
}

function compileRx(pattern) {
  if (pattern instanceof RegExp) return pattern;
  if (typeof pattern !== 'string' || !pattern) return null;
  try { return new RegExp(pattern); } catch (_) { return null; }
}

let merchantExtensionsApplied = false;
function applyMerchantExtensions() {
  if (merchantExtensionsApplied) return;
  merchantExtensionsApplied = true;
  const settings = (typeof window !== 'undefined' && window.accessProCookieBannerSettings) || null;
  if (!settings || typeof settings !== 'object') return;
  mergeArrayMap(COOKIE_PATTERNS, settings.cookie_patterns_extra, compileRx);
}

function isGoogleConsentModeEnabled() {
  const settings = (typeof window !== 'undefined' && window.accessProCookieBannerSettings) || null;
  if (!settings || typeof settings !== 'object') return false;
  return settings.google_consent_mode === true;
}

function isMicrosoftConsentModeEnabled() {
  const settings = (typeof window !== 'undefined' && window.accessProCookieBannerSettings) || null;
  if (!settings || typeof settings !== 'object') return true;
  return settings.microsoft_consent_mode !== false;
}

function isGpcEnabled() {
  const settings = (typeof window !== 'undefined' && window.accessProCookieBannerSettings) || null;
  if (!settings || typeof settings !== 'object') return true;
  return settings.global_privacy_control !== false;
}

/**
 * True when Global Privacy Control is enabled in settings and the browser
 * sends navigator.globalPrivacyControl === true (Do Not Sell/Share signal).
 */
function isGpcSignalActive() {
  if (!isGpcEnabled()) return false;
  try {
    return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
  } catch (_) {
    return false;
  }
}

// ---------- consent state helpers ----------

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function getConsentModel() {
  try {
    const settings =
      (typeof window !== 'undefined' && window.accessProCookieBannerSettings) || null;
    return (settings && settings.consent_model) || null;
  } catch (_) {
    return null;
  }
}

function shouldDefaultAllowNonEssential() {
  const consent = readConsent();
  if (consent && consent.categories) return false;
  const region = getRegionStateSync();
  if (region && region.isStrict) return false;
  return getConsentModel() === 'do_not_sell';
}

// ---------- Google Consent Mode v2 ----------

let googleDefaultsInstalled = false;
let microsoftDefaultsInstalled = false;

/**
 * Install Google Consent Mode v2 default-denied state.
 * Must run as early as possible, ideally before any GA/GTM script executes.
 */
export function setupConsentModeDefaults() {
  if (googleDefaultsInstalled) return;
  googleDefaultsInstalled = true;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  const defaultState = shouldDefaultAllowNonEssential() ? 'granted' : 'denied';
  // GPC forces ad-related storage denied (Do Not Sell/Share).
  const adState = isGpcSignalActive() ? 'denied' : defaultState;
  window.gtag('consent', 'default', {
    ad_storage:              adState,
    ad_user_data:            adState,
    ad_personalization:      adState,
    analytics_storage:       defaultState,
    functionality_storage:   defaultState,
    personalization_storage: defaultState,
    security_storage:        'granted',
    wait_for_update:         500,
  });
  // Some hosts push consent default=granted before this script loads.
  // Google keeps the first `default` and ignores later ones; `update`
  // is the only command that can override that granted state.
  updateConsentMode({
    marketing:    adState === 'granted',
    statistics:   defaultState === 'granted',
    preferences:  defaultState === 'granted',
  });
}

function updateConsentMode(categories) {
  if (typeof window.gtag !== 'function') return;
  const marketingGranted = !!categories.marketing && !isGpcSignalActive();
  window.gtag('consent', 'update', {
    ad_storage:              marketingGranted       ? 'granted' : 'denied',
    ad_user_data:            marketingGranted       ? 'granted' : 'denied',
    ad_personalization:      marketingGranted       ? 'granted' : 'denied',
    analytics_storage:       categories.statistics  ? 'granted' : 'denied',
    functionality_storage:   categories.preferences ? 'granted' : 'denied',
    personalization_storage: categories.preferences ? 'granted' : 'denied',
  });
}

// ---------- Microsoft UET & Clarity Consent Mode ----------

function ensureClarityStub() {
  if (typeof window.clarity === 'function') return;
  window.clarity = function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };
}

/**
 * Install Microsoft UET + Clarity Consent Mode default-denied state.
 * Must run as early as possible, ideally before UET / Clarity scripts execute.
 */
export function setupMicrosoftConsentModeDefaults() {
  if (microsoftDefaultsInstalled) return;
  microsoftDefaultsInstalled = true;
  const defaultState = shouldDefaultAllowNonEssential() ? 'granted' : 'denied';
  const adState = isGpcSignalActive() ? 'denied' : defaultState;

  window.uetq = window.uetq || [];
  window.uetq.push('consent', 'default', {
    ad_storage: adState,
    wait_for_update: 500,
  });

  ensureClarityStub();
  window.clarity('consentv2', {
    ad_Storage: adState,
    analytics_Storage: defaultState,
  });
}

/**
 * Set Consent Mode defaults immediately on script parse — before config fetch /
 * region detection — so GTM/GA see denied storage as early as possible.
 * Only runs when Google Consent Mode v2 is already enabled in settings.
 * If the visitor already has stored consent, push an immediate `update`.
 */
export function installEarlyConsentDefaults() {
  if (!isGoogleConsentModeEnabled()) return;
  try { setupConsentModeDefaults(); } catch (_) {}
  const stored = readConsent();
  if (stored && stored.categories) {
    try { updateConsentMode(stored.categories); } catch (_) {}
  }
}

function updateMicrosoftConsentMode(categories) {
  const adStorage = categories.marketing && !isGpcSignalActive() ? 'granted' : 'denied';
  const analyticsStorage = categories.statistics ? 'granted' : 'denied';

  window.uetq = window.uetq || [];
  window.uetq.push('consent', 'update', {
    ad_storage: adStorage,
  });

  ensureClarityStub();
  window.clarity('consentv2', {
    ad_Storage: adStorage,
    analytics_Storage: analyticsStorage,
  });
}

// ---------- merchant-gated script activation ----------

function activateScriptElement(el) {
  if (!el || el.dataset.acbActivated === '1') return;
  el.dataset.acbActivated = '1';

  const real = document.createElement('script');
  for (const attr of Array.from(el.attributes)) {
    if (attr.name === 'type' || attr.name === 'data-acb-src' || attr.name === 'data-acb-category') continue;
    real.setAttribute(attr.name, attr.value);
  }
  const src = el.getAttribute('data-acb-src') || el.getAttribute('src');
  if (src) {
    real.src = src;
  } else if (el.textContent) {
    real.text = el.textContent;
  }
  el.parentNode?.insertBefore(real, el.nextSibling);
}

export function activateScriptsForCategory(category) {
  document
    .querySelectorAll(`script[type="text/plain"][data-acb-category="${category}"]`)
    .forEach(activateScriptElement);
}

// ---------- cookie cleanup ----------

function deleteCookie(name) {
  const host  = window.location.hostname;
  const parts = host.split('.');
  const domains = new Set(['', host]);
  for (let i = 0; i < parts.length - 1; i++) {
    domains.add('.' + parts.slice(i).join('.'));
  }
  domains.forEach(domain => {
    const tail = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${tail}`;
  });
}

function clearCookiesForCategory(category) {
  const patterns = COOKIE_PATTERNS[category];
  if (!patterns || !patterns.length) return;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  cookies.forEach(part => {
    const name = (part.split('=')[0] || '').trim();
    if (!name) return;
    if (patterns.some(rx => rx.test(name))) deleteCookie(name);
  });
}

function clearAllDeniedCookies() {
  // Cookie cleanup only when Google Consent Mode v2 is enabled.
  if (!isGoogleConsentModeEnabled()) return;
  const consent = readConsent();
  if ((!consent || !consent.categories) && shouldDefaultAllowNonEssential()) return;
  const granted = (consent && consent.categories) || {};
  Object.keys(COOKIE_PATTERNS).forEach(cat => {
    if (!granted[cat]) clearCookiesForCategory(cat);
  });
}

// ---------- DOM observer (opt-in text/plain scripts only) ----------

let domObserverStarted = false;
function startDomObserver() {
  if (domObserverStarted || typeof MutationObserver === 'undefined') return;
  domObserverStarted = true;
  const obs = new MutationObserver(records => {
    for (const rec of records) {
      rec.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.tagName !== 'SCRIPT' || node.getAttribute('type') !== 'text/plain') return;
        const cat = node.getAttribute('data-acb-category');
        if (!cat) return;
        const consent = readConsent();
        if (consent && consent.categories && consent.categories[cat]) {
          activateScriptElement(node);
        }
      });
    }
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });
}

// ---------- periodic cleanup ----------

const CLEANUP_INTERVAL_MS = 30000;
let cleanupTimer = null;
let cleanupVisibilityBound = false;

function startPeriodicCleanup() {
  if (cleanupTimer) return;
  cleanupTimer = setInterval(clearAllDeniedCookies, CLEANUP_INTERVAL_MS);
  if (!cleanupVisibilityBound) {
    cleanupVisibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') clearAllDeniedCookies();
    });
  }
}

// ---------- public API ----------

export function applyConsent(consent) {
  if (!consent || !consent.categories) return;
  const a = consent.action;
  if (a === 'accept' || a === 'reject' || a === 'do_not_sell') {
    logVisitorRegion(
      a === 'accept' ? 'accept' : a === 'do_not_sell' ? 'do not sell' : 'reject',
    );
  }
  if (isGoogleConsentModeEnabled()) updateConsentMode(consent.categories);
  if (isMicrosoftConsentModeEnabled()) updateMicrosoftConsentMode(consent.categories);
  Object.keys(consent.categories).forEach(cat => {
    // GPC overrides a prior marketing grant (Do Not Sell/Share).
    if (consent.categories[cat] && !(cat === 'marketing' && isGpcSignalActive())) {
      activateScriptsForCategory(cat);
    } else if (isGoogleConsentModeEnabled()) {
      // Only clear denied-category cookies when Consent Mode v2 is on.
      clearCookiesForCategory(cat);
    }
  });
}

let gatingStarted = false;
export function initGating(existingConsent) {
  if (gatingStarted) {
    if (existingConsent) applyConsent(existingConsent);
    return;
  }
  gatingStarted = true;

  // Merge merchant-supplied cookie patterns before cleanup runs.
  applyMerchantExtensions();

  try {
    if (isGoogleConsentModeEnabled()) setupConsentModeDefaults();
  } catch (_) {}
  try {
    if (isMicrosoftConsentModeEnabled()) setupMicrosoftConsentModeDefaults();
  } catch (_) {}

  // Honor GPC + Consent Mode: clear marketing cookies only when GCM is on.
  if (isGoogleConsentModeEnabled() && isGpcSignalActive() && !existingConsent) {
    try { clearCookiesForCategory('marketing'); } catch (_) {}
  }

  if (existingConsent) applyConsent(existingConsent);

  window.addEventListener('accessproCookieConsent', e => applyConsent(e.detail));
  window.addEventListener('accessproCookieConsentWithdrawn', () => {
    applyConsent({
      categories: {
        necessary:   true,
        statistics:  false,
        marketing:   false,
        preferences: false,
      },
    });
  });

  startDomObserver();
  startPeriodicCleanup();
}
